
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './App';
import { useAuth } from './App';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, where, Timestamp } from 'firebase/firestore';

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} disabled={!text} className={'inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-all ' + (copied ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200') + ' disabled:opacity-40'}>
      <span>{copied ? 'Copied!' : (label || 'Copy')}</span>
    </button>
  );
};

const CopyField = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <div className="flex items-center space-x-2">
      <span className={'text-sm text-slate-800 ' + (mono ? 'font-mono' : '')}>{value || '-'}</span>
      <CopyButton text={value} />
    </div>
  </div>
);

// QR Code Modal Component
const QRCodeModal = ({ isOpen, onClose, shopId, shopName }) => {
  const [copied, setCopied] = useState(false);
  const intakeUrl = `${window.location.origin}/s/${shopId}/intake`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(intakeUrl)}`;

  if (!isOpen) return null;

  const copyLink = async () => {
    await navigator.clipboard.writeText(intakeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${(shopName || 'shop').replace(/\s+/g, '-').toLowerCase()}-checkin-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printQR = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Customer Check-In QR Code</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
            h1 { font-size: 28px; margin-bottom: 10px; }
            h2 { font-size: 20px; color: #666; margin-bottom: 30px; font-weight: normal; }
            img { margin: 20px 0; }
            .url { font-size: 14px; color: #888; margin-top: 30px; word-break: break-all; }
          </style>
        </head>
        <body>
          <h1>${shopName || 'Customer Check-In'}</h1>
          <h2>Scan to Check In Your Vehicle</h2>
          <img src="${qrUrl}" alt="QR Code" width="300" height="300" />
          <p class="url">${intakeUrl}</p>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-800 mb-2">Customer Check-In Link</h2>
          <p className="text-slate-500 text-sm mb-4">Share this with your customers</p>
          
          {/* URL Display */}
          <div className="bg-slate-100 rounded-lg p-3 mb-4">
            <p className="text-sm font-mono text-slate-700 break-all">{intakeUrl}</p>
            <button 
              onClick={copyLink}
              className="mt-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition"
            >
              {copied ? '✓ Copied!' : 'Copy Link'}
            </button>
          </div>

          {/* QR Code */}
          <div className="bg-white border-2 border-slate-200 rounded-xl p-4 inline-block mb-4">
            <img src={qrUrl} alt="QR Code" className="w-48 h-48" />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 justify-center">
            <button
              onClick={downloadQR}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
            <button
              onClick={printQR}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
          </div>

          <button
            onClick={onClose}
            className="mt-6 text-slate-500 hover:text-slate-700 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PrintableAuthorization = React.forwardRef(({ intake, shop }, ref) => {
  if (!intake) return null;
  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'long' });
  };
  return (
    <div ref={ref} className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="border-b-4 border-slate-800 pb-4 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{shop && shop.shopName ? shop.shopName : 'Auto Shop'}</h1>
            <p className="text-sm text-slate-600">{shop && shop.address && shop.address.full ? shop.address.full : ''}</p>
            <p className="text-sm text-slate-600">{shop && shop.ownerPhone ? shop.ownerPhone : ''}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-800">AUTHORIZATION TO OPERATE</p>
            <p className="text-sm text-slate-600">Transaction ID: <span className="font-mono font-bold">{intake.transactionId}</span></p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8 mb-6">
        <div>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 border-b pb-1">Customer Information</h2>
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="py-1 text-slate-500 w-24">Name:</td><td className="py-1 font-medium">{intake.customer && intake.customer.fullName}</td></tr>
              <tr><td className="py-1 text-slate-500">Phone:</td><td className="py-1">{intake.customer && intake.customer.phone}</td></tr>
              <tr><td className="py-1 text-slate-500">Email:</td><td className="py-1">{intake.customer && intake.customer.email}</td></tr>
              <tr><td className="py-1 text-slate-500">Address:</td><td className="py-1">{intake.address && intake.address.full}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 border-b pb-1">Vehicle Information</h2>
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="py-1 text-slate-500 w-24">Vehicle:</td><td className="py-1 font-medium">{intake.vehicle && intake.vehicle.fullDescription}</td></tr>
              <tr><td className="py-1 text-slate-500">Plate:</td><td className="py-1 font-mono">{intake.vehicle && intake.vehicle.licensePlate ? intake.vehicle.licensePlate : 'N/A'}</td></tr>
              <tr><td className="py-1 text-slate-500">VIN:</td><td className="py-1 font-mono text-xs">{intake.vehicle && intake.vehicle.vin ? intake.vehicle.vin : 'N/A'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="mb-6">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 border-b pb-1">Service Request</h2>
        {intake.service && intake.service.techSummary ? (
          <div className="p-3 bg-slate-50 rounded border text-sm">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">AI Tech Summary</p>
            <p className="text-slate-700 whitespace-pre-wrap">{intake.service.techSummary}</p>
          </div>
        ) : intake.service && intake.service.reasonForVisit ? (
          <div className="p-3 bg-slate-50 rounded border text-sm">
            <p className="text-sm font-medium text-slate-800">{intake.service.reasonForVisit}</p>
            {intake.service.customerDescription && (
              <p className="mt-1 text-slate-600 italic">{intake.service.customerDescription}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No summary available</p>
        )}
        {intake.service && intake.service.transcript && intake.service.transcript.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Customer Conversation Transcript</p>
            <div className="space-y-1 text-xs">
              {intake.service.transcript.map((msg, i) => (
                <p key={i} className={msg.role === 'model' ? 'text-slate-500' : 'text-slate-800'}>
                  <span className="font-bold">{msg.role === 'model' ? ((intake.service.advisorName || 'Kenneth') + ':') : 'Customer:'}</span> {msg.text}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="mb-6 p-4 border-2 border-slate-300 rounded">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Authorization Statement</h2>
        <p className="text-xs text-slate-700 leading-relaxed">
          By signing below, I authorize the repair facility to operate, test drive, and perform diagnostic and repair services on the vehicle described above. I acknowledge and agree to the shop terms regarding mechanic lien rights, storage charges, limited liability, and payment requirements. I consent to receive communications via phone, text, and email pursuant to the TCPA. This electronic signature is legally binding under the ESIGN Act and UETA.
        </p>
      </div>
      <div className="mb-8">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 border-b pb-1">Customer Signature</h2>
        <div className="flex items-end space-x-8">
          <div className="flex-1">
            {intake.legalCompliance && intake.legalCompliance.signatureUrl && (
              <img src={intake.legalCompliance.signatureUrl} alt="Signature" className="h-16 border-b-2 border-slate-800" />
            )}
            <p className="text-xs text-slate-500 mt-1">{intake.customer && intake.customer.fullName}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{formatDate(intake.createdAt)}</p>
            <p className="text-xs text-slate-500">Date & Time of Signature</p>
          </div>
        </div>
      </div>
      <div className="border-t-2 border-slate-200 pt-4 mt-8">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Digital Signature Audit Trail</h3>
        <div className="grid grid-cols-2 gap-4 text-[10px] text-slate-500 font-mono">
          <div>
            <p>Signed At (UTC): {intake.auditTrail && intake.auditTrail.timestampUTC}</p>
            <p>IP Address: {intake.auditTrail && intake.auditTrail.ipAddress}</p>
            <p>Disclaimer Ver: {intake.legalCompliance && intake.legalCompliance.disclaimerVersion}</p>
          </div>
          <div>
            <p>Device: {intake.auditTrail && intake.auditTrail.deviceSimplified}</p>
            <p>Browser: {intake.auditTrail && intake.auditTrail.browser} ({intake.auditTrail && intake.auditTrail.operatingSystem})</p>
            <p>Resolution: {intake.auditTrail && intake.auditTrail.screenResolution}</p>
          </div>
        </div>
        <div className="flex justify-between items-center text-xs text-slate-400 mt-4 pt-2 border-t border-slate-100">
          <p>Electronic signature verified via secure HTTPS connection. Compliant with ESIGN Act & UETA.</p>
          <p className="font-mono">{intake.transactionId}</p>
        </div>
      </div>
    </div>
  );
});

const getStatusColor = (status) => {
  switch(status) {
    case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'in-progress': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'archived': return 'bg-slate-50 text-slate-500 border-slate-200';
    default: return 'bg-slate-50 text-slate-600 border-slate-200';
  }
};

const getTimeAgo = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins + 'm ago';
  if (mins < 1440) return Math.floor(mins / 60) + 'h ago';
  return Math.floor(mins / 1440) + 'd ago';
};

const IntakeCard = ({ intake, shop, onStatusChange, expanded, onToggle }) => {
  const [showAudit, setShowAudit] = useState(false);
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Print</title>');
    printWindow.document.write('<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">');
    printWindow.document.write('</head><body>');
    printWindow.document.write(content.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.print();
    };
  };

  const copyAll = () => {
    const text = 'Customer: ' + (intake.customer && intake.customer.fullName) + '\n' +
      'Phone: ' + (intake.customer && intake.customer.phone) + '\n' +
      'Email: ' + (intake.customer && intake.customer.email) + '\n' +
      'Address: ' + (intake.address && intake.address.full) + '\n\n' +
      'Vehicle: ' + (intake.vehicle && intake.vehicle.fullDescription) + '\n' +
      'Plate: ' + (intake.vehicle && intake.vehicle.licensePlate ? intake.vehicle.licensePlate : 'N/A') + '\n' +
      'VIN: ' + (intake.vehicle && intake.vehicle.vin ? intake.vehicle.vin : 'N/A') + '\n' +
      'Service: ' + (intake.service && (intake.service.techSummary || intake.service.reasonForVisit) ? (intake.service.techSummary || intake.service.reasonForVisit) : 'None');
    navigator.clipboard.writeText(text);
  };

  return (
    <div className={'bg-white rounded-xl shadow-lg border-l-4 overflow-hidden transition-all ' + (intake.status === 'pending' ? 'border-l-amber-400' : intake.status === 'in-progress' ? 'border-l-blue-400' : 'border-l-emerald-400')}>
      <div className="px-6 py-4 cursor-pointer hover:bg-slate-50" onClick={onToggle}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-bold text-slate-800">{intake.customer && intake.customer.fullName}</h3>
              <span className={'px-2 py-0.5 rounded-full text-xs font-medium border ' + getStatusColor(intake.status)}>
                {intake.status ? intake.status.toUpperCase() : 'PENDING'}
              </span>
              {intake.status === 'pending' && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
              )}
            </div>
            <p className="text-slate-600 mt-1">{intake.vehicle && intake.vehicle.fullDescription}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">{getTimeAgo(intake.createdAt)}</p>
            <p className="text-xs font-mono text-slate-400">{intake.transactionId}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-600">{intake.customer && intake.customer.phone}</span>
            <CopyButton text={intake.customer && intake.customer.phone} />
          </div>
          <span className="px-2 py-1 bg-slate-100 rounded text-sm text-slate-600">
            {intake.service && intake.service.transcript
              ? (() => { const m = intake.service.transcript.find(x => x.role === 'user'); return m ? (m.text.length > 55 ? m.text.slice(0, 55) + '…' : m.text) : 'AI Intake'; })()
              : (intake.service && intake.service.reasonForVisit) || 'No service info'}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-700 mb-3">Customer</h4>
              <div className="bg-white rounded-lg p-3 border">
                {intake.customer && intake.customer.firstName ? (
                  <>
                    <CopyField label="First Name" value={intake.customer.firstName} />
                    <CopyField label="Last Name" value={intake.customer.lastName} />
                  </>
                ) : (
                  <CopyField label="Name" value={intake.customer && intake.customer.fullName} />
                )}
                <CopyField label="Phone" value={intake.customer && intake.customer.phone} />
                <CopyField label="Email" value={intake.customer && intake.customer.email} />
                <CopyField label="Address" value={intake.address && intake.address.full} />
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 mb-3">Vehicle</h4>
              <div className="bg-white rounded-lg p-3 border">
                <CopyField label="Vehicle" value={intake.vehicle && intake.vehicle.fullDescription} />
                <CopyField label="Plate" value={intake.vehicle && intake.vehicle.licensePlate} mono={true} />
                <CopyField label="VIN" value={intake.vehicle && intake.vehicle.vin} mono={true} />
              </div>
              {((intake.photos && intake.photos.licensePlate) || (intake.photos && intake.photos.vin)) && (
                <div className="flex space-x-3 mt-3">
                  {intake.photos && intake.photos.licensePlate && (
                    <a href={intake.photos.licensePlate} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={intake.photos.licensePlate} alt="Plate" className="h-16 rounded-lg border hover:opacity-80" />
                    </a>
                  )}
                  {intake.photos && intake.photos.vin && (
                    <a href={intake.photos.vin} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={intake.photos.vin} alt="VIN" className="h-16 rounded-lg border hover:opacity-80" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <h4 className="font-semibold text-slate-700 mb-3">Service Request</h4>
            {/* AI Tech Summary — front and center */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-amber-500 text-base">⚡</span>
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">{(intake.service && intake.service.advisorName) || 'Kenneth'}'s Tech Summary</p>
                </div>
                <CopyButton text={intake.service && intake.service.techSummary} label="Copy Summary" />
              </div>
              {intake.service && intake.service.techSummary ? (
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{intake.service.techSummary}</p>
              ) : intake.service && intake.service.reasonForVisit ? (
                <div>
                  <p className="text-sm text-slate-800 font-medium">{intake.service.reasonForVisit}</p>
                  {intake.service.customerDescription && (
                    <p className="text-sm text-slate-600 italic mt-1">{intake.service.customerDescription}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">Summary generating — check back in a moment.</p>
              )}
            </div>
            {/* Collapsible transcript */}
            {intake.service && intake.service.transcript && intake.service.transcript.length > 0 && (
              <details className="bg-white border rounded-lg overflow-hidden">
                <summary className="px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none flex items-center justify-between list-none">
                  <span>💬 {(intake.service && intake.service.advisorName) || 'Kenneth'} Transcript <span className="text-slate-400 font-normal">({intake.service.transcript.length} messages)</span></span>
                  <span className="text-slate-400 text-xs">tap to expand</span>
                </summary>
                <div className="px-4 pb-4 pt-2 space-y-2 max-h-72 overflow-y-auto border-t">
                  {intake.service.transcript.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : ''}`}>
                      <div className={`max-w-sm px-3 py-2 rounded-lg text-xs leading-relaxed ${msg.role === 'model' ? 'bg-slate-100 text-slate-700' : 'bg-amber-50 text-slate-800'}`}>
                        <span className="font-bold block text-[10px] text-slate-400 mb-0.5">{msg.role === 'model' ? (intake.service.advisorName || 'Kenneth') : 'Customer'}</span>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>

          <div className="mt-4">
            <button onClick={() => setShowAudit(!showAudit)} className="text-sm text-slate-500 hover:text-slate-700 flex items-center">
              <span className={'mr-1 transition-transform ' + (showAudit ? 'rotate-90' : '')}>▶</span>
              Digital Signature Audit Trail
            </button>
            {showAudit && (
              <div className="mt-3 p-4 bg-slate-100 rounded-lg text-xs font-mono space-y-1">
                <p><span className="text-slate-500">Transaction ID:</span> <span className="font-bold">{intake.transactionId}</span></p>
                <p><span className="text-slate-500">UTC Timestamp:</span> {intake.auditTrail && intake.auditTrail.timestampUTC}</p>
                <p><span className="text-slate-500">IP Address:</span> {intake.auditTrail && intake.auditTrail.ipAddress}</p>
                <p><span className="text-slate-500">Device:</span> {intake.auditTrail && intake.auditTrail.deviceSimplified}</p>
                <p><span className="text-slate-500">User Agent:</span> <span className="text-xs break-all">{intake.auditTrail && intake.auditTrail.userAgent}</span></p>
                {intake.legalCompliance && intake.legalCompliance.signatureUrl && (
                  <div className="pt-2 border-t border-slate-300 mt-2">
                    <p className="text-slate-500 mb-2">Captured Signature:</p>
                    <img src={intake.legalCompliance.signatureUrl} alt="Signature" className="h-12 bg-white rounded border" />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-slate-200">
            {intake.status === 'pending' && (
              <button onClick={() => onStatusChange(intake.id, 'in-progress')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                Start Work
              </button>
            )}
            {intake.status === 'in-progress' && (
              <button onClick={() => onStatusChange(intake.id, 'completed')} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                Mark Complete
              </button>
            )}
            {(intake.status === 'completed' || intake.status === 'in-progress') && (
              <button onClick={() => onStatusChange(intake.id, 'archived')} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-300">
                Archive
              </button>
            )}
            <button onClick={handlePrint} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Auth
            </button>
            <button onClick={copyAll} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200">
              Copy All
            </button>
          </div>
        </div>
      )}

      <div className="hidden">
        <PrintableAuthorization ref={printRef} intake={intake} shop={shop} />
      </div>
    </div>
  );
};

const AdvisorDash = () => {
  const navigate = useNavigate();
  const { shopId, shopData, logout, isAdmin } = useAuth();
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ pending: 0, inProgress: 0, completed: 0, today: 0 });
  const [showQRModal, setShowQRModal] = useState(false);
  
  // New: Admin View Toggle (defaults to 'mine' to prevent bleeding)
  const [viewMode, setViewMode] = useState('mine'); 

  useEffect(() => {
    // Safety check: If not admin and no shopId, stop.
    if (!shopId && !isAdmin) {
      setLoading(false);
      return;
    }

    const ref = collection(db, 'intakes');
    let q;

    // Logic: Only show global if Admin explicitly asks for it. 
    // Otherwise, scope to the specific shopId (even for Admins).
    if (isAdmin && viewMode === 'global') {
      q = query(ref, orderBy('createdAt', 'desc'));
    } else {
      // If no shopId is assigned (e.g. pure platform admin), fallback to global or empty? 
      // Better to query by shopId if it exists.
      if (shopId) {
        q = query(ref, where('shopId', '==', shopId), orderBy('createdAt', 'desc'));
      } else if (isAdmin) {
        // Edge case: Admin with no shop assigned trying to view 'mine'. 
        // Force global to avoid empty screen, or show "No Shop Assigned".
        // Let's force global if they have no shopId.
        q = query(ref, orderBy('createdAt', 'desc'));
      } else {
        // Should not happen due to initial check
        setLoading(false);
        return;
      }
    }

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setIntakes(data);
      setLoading(false);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setStats({
        pending: data.filter(i => i.status === 'pending').length,
        inProgress: data.filter(i => i.status === 'in-progress').length,
        completed: data.filter(i => i.status === 'completed').length,
        today: data.filter(i => i.createdAt && i.createdAt.toDate && i.createdAt.toDate() >= today).length
      });

      const newest = data.find(i => i.status === 'pending');
      if (newest && !expandedId) setExpandedId(newest.id);
    });

    return () => unsub();
  }, [shopId, isAdmin, expandedId, viewMode]);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'intakes', id), { status: status, updatedAt: Timestamp.now() });
  };

  const filtered = intakes
    .filter(i => filter === 'all' ? i.status !== 'archived' : i.status === filter)
    .filter(i => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (i.customer && i.customer.fullName && i.customer.fullName.toLowerCase().includes(q)) ||
        (i.customer && i.customer.phone && i.customer.phone.includes(q)) ||
        (i.vehicle && i.vehicle.fullDescription && i.vehicle.fullDescription.toLowerCase().includes(q)) ||
        (i.vehicle && i.vehicle.licensePlate && i.vehicle.licensePlate.toLowerCase().includes(q)) ||
        (i.vehicle && i.vehicle.vin && i.vehicle.vin.toLowerCase().includes(q)) ||
        (i.transactionId && i.transactionId.toLowerCase().includes(q));
    });

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white sticky top-0 z-50 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                <span className="text-amber-400">*</span> {viewMode === 'global' ? 'Global Feed (Admin)' : (shopData && shopData.shopName ? shopData.shopName : 'Dashboard')}
              </h1>
              <p className="text-slate-400 text-sm">Service Advisor Dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm text-slate-400">Today</p>
                <p className="text-xl font-bold">{stats.today} check-ins</p>
              </div>
              
              {/* Admin Toggle */}
              {isAdmin && (
                <div className="bg-slate-800 rounded p-1 flex text-xs font-medium">
                   <button 
                     onClick={() => setViewMode('mine')}
                     className={'px-3 py-1 rounded transition-all ' + (viewMode === 'mine' ? 'bg-amber-500 text-slate-900' : 'text-slate-400 hover:text-white')}
                   >
                     My Shop
                   </button>
                   <button 
                     onClick={() => setViewMode('global')}
                     className={'px-3 py-1 rounded transition-all ' + (viewMode === 'global' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-white')}
                   >
                     All Shops
                   </button>
                </div>
              )}

              {stats.pending > 0 && (
                <div className="flex items-center space-x-1 px-3 py-1 bg-amber-500 rounded-full animate-pulse">
                  <span className="font-bold text-sm">{stats.pending} New</span>
                </div>
              )}
              {isAdmin && <button onClick={() => navigate('/admin')} className="px-3 py-1 bg-red-600 rounded text-sm">Admin Panel</button>}
              <button onClick={() => navigate('/settings')} className="px-3 py-1 bg-slate-700 rounded text-sm hover:bg-slate-600 transition">Settings</button>
              <button onClick={() => { logout(); navigate('/login'); }} className="px-3 py-1 bg-slate-600 rounded text-sm hover:bg-slate-500 transition">Logout</button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'all', label: 'Active', count: stats.pending + stats.inProgress },
                { key: 'pending', label: 'Pending', count: stats.pending },
                { key: 'in-progress', label: 'In Progress', count: stats.inProgress },
                { key: 'completed', label: 'Completed', count: stats.completed },
                { key: 'archived', label: 'Archived', count: undefined }
              ].map(item => (
                <button
                  key={item.key}
                  onClick={() => setFilter(item.key)}
                  className={'px-3 py-2 rounded-lg font-medium text-sm transition-all ' + (filter === item.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                >
                  {item.label} {item.count !== undefined && <span className={'ml-1 px-1.5 py-0.5 rounded-full text-xs ' + (filter === item.key ? 'bg-slate-700' : 'bg-slate-200')}>{item.count}</span>}
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg w-48 sm:w-64 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-slate-600">No intakes found</h3>
            <p className="text-slate-500">{search ? 'Try adjusting your search' : 'Waiting for customer check-ins...'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(intake => (
              <IntakeCard
                key={intake.id}
                intake={intake}
                shop={shopData}
                onStatusChange={updateStatus}
                expanded={expandedId === intake.id}
                onToggle={() => setExpandedId(expandedId === intake.id ? null : intake.id)}
              />
            ))}
          </div>
        )}
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-slate-500">Showing {filtered.length} intakes</span>
            <span className="text-xs text-slate-400 hidden sm:inline">• Images retained 30 days</span>
          </div>
          {shopId && (
            <button 
              onClick={() => setShowQRModal(true)}
              className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
              Customer Check-In Link & QR
            </button>
          )}
        </div>
      </footer>

      {/* QR Code Modal */}
      <QRCodeModal 
        isOpen={showQRModal} 
        onClose={() => setShowQRModal(false)} 
        shopId={shopId}
        shopName={shopData?.shopName}
      />
    </div>
  );
};

export default AdvisorDash;
